import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/MultiSelect';
import { Switch } from '@/components/ui/Switch';
import { t } from '@/lib/i18n';
import type { EditorState } from '../roleContractEditorState';

interface Props {
  state: EditorState;
  setState: React.Dispatch<React.SetStateAction<EditorState>>;
  metricOptions: MultiSelectOption[];
  metricsData: Array<{ id: string; title?: string; name?: string }>;
}

export function OutcomesSection({ state, setState, metricOptions, metricsData }: Props) {
  // Sven dogfood 2026-05-03: defensively filter out empty / orphaned
  // entries before rendering. The screenshot showed an extra blank
  // row in the per-metric toggle list — root cause was a state entry
  // with `metricId: ''` (or pointing at a metric that no longer exists
  // in metricsData). Both yield a row with no name and a dangling
  // toggle. Filter them out for display AND remove them from state on
  // mount so the next save doesn't re-persist the orphan.
  const validOutcomes = state.outcomesOwnership.filter(
    (o) => !!o.metricId && metricsData.some((m) => m.id === o.metricId),
  );

  React.useEffect(() => {
    if (validOutcomes.length !== state.outcomesOwnership.length) {
      setState((s) => ({ ...s, outcomesOwnership: validOutcomes }));
    }
    // Run once when the metrics catalogue arrives. Don't put state in
    // deps — that would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricsData.length]);

  return (
    <Card>
      {/* Sven dogfood 2026-05-03: heading said "Outcomes Ownership" but
          the field below is "Owned metrics" — confusing. Aligning the
          heading with what the user actually picks. */}
      <CardHeader><CardTitle>{t('Owned Metrics')}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t('Pick the metrics this role is accountable for. Toggle write-access for each (write = can update value, read = can view only).')}
        </p>
        <MultiSelect
          label={t('Owned metrics')}
          options={metricOptions}
          value={validOutcomes.map((o) => o.metricId)}
          onChange={(ids) =>
            setState((s) => ({
              ...s,
              outcomesOwnership: ids
                .filter((mid) => !!mid)
                .map((mid) => {
                  const existing = s.outcomesOwnership.find((o) => o.metricId === mid);
                  const meta = metricsData.find((m) => m.id === mid);
                  return existing ?? { metricId: mid, metricName: meta?.title ?? meta?.name, access: 'read' as const };
                }),
            }))
          }
        />
        {validOutcomes.length > 0 && (
          <div className="space-y-2 mt-2">
            {validOutcomes.map((o) => {
              const meta = metricsData.find((m) => m.id === o.metricId);
              const displayName = o.metricName ?? meta?.title ?? meta?.name ?? o.metricId;
              return (
                <div key={o.metricId} className="flex items-center justify-between p-2 border rounded">
                  <span className="text-sm">{displayName}</span>
                  <Switch
                    checked={o.access === 'write'}
                    onChange={(e) =>
                      setState((s) => ({
                        ...s,
                        outcomesOwnership: s.outcomesOwnership.map((x) =>
                          x.metricId === o.metricId ? { ...x, access: e.target.checked ? 'write' : 'read' } : x,
                        ),
                      }))
                    }
                    label={o.access === 'write' ? t('Write') : t('Read-only')}
                  />
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
